/**
 * Utility functions for handling filter IDs.
 * `destinationId` is a single string, `destinationIds` is an array.
 * Same pattern for `landmarkId` and `landmarkIds`.
 */

/**
 * Gets destination IDs from filters.
 * Returns `destinationIds` if available, otherwise wraps `destinationId` in an array.
 *
 * @example
 * getDestinationIds({ destinationIds: ['a', 'b'] })  // ['a', 'b']
 * getDestinationIds({ destinationId: 'a' })          // ['a']
 * getDestinationIds({})                               // undefined
 */
export function getDestinationIds(filters: {
  destinationId?: string
  destinationIds?: Array<string>
}): Array<string> | undefined {
  if (filters.destinationIds && filters.destinationIds.length > 0) {
    return filters.destinationIds
  }
  if (filters.destinationId) {
    return [filters.destinationId]
  }
  return undefined
}

/**
 * Gets landmark IDs from filters.
 * Returns `landmarkIds` if available, otherwise wraps `landmarkId` in an array.
 *
 * @example
 * getLandmarkIds({ landmarkIds: ['a', 'b'] })  // ['a', 'b']
 * getLandmarkIds({ landmarkId: 'a' })          // ['a']
 * getLandmarkIds({})                            // undefined
 */
export function getLandmarkIds(filters: {
  landmarkId?: string
  landmarkIds?: Array<string>
}): Array<string> | undefined {
  if (filters.landmarkIds && filters.landmarkIds.length > 0) {
    return filters.landmarkIds
  }
  if (filters.landmarkId) {
    return [filters.landmarkId]
  }
  return undefined
}

/**
 * Formats ID array for API requests.
 * Returns comma-separated string for multiple IDs, single value for one ID.
 *
 * @example
 * formatIdsForSearchApi(['a', 'b']) // 'a,b'
 * formatIdsForSearchApi(['a'])      // 'a'
 * formatIdsForSearchApi(undefined)  // undefined
 */
export function formatIdsForSearchApi(ids: Array<string> | undefined): string | undefined {
  if (!ids || ids.length === 0) return undefined
  return ids.length > 1 ? ids.join(',') : ids[0]
}

/**
 * Gets formatted place IDs from filters, prioritizing destinations over landmarks.
 * Useful for API requests that accept a single placeIds parameter.
 *
 * @example
 * getPlaceIdsForSearchApi({ destinationIds: ['a', 'b'] })  // 'a,b'
 * getPlaceIdsForSearchApi({ destinationId: 'a' })          // 'a'
 * getPlaceIdsForSearchApi({ landmarkId: 'x' })             // 'x'
 * getPlaceIdsForSearchApi({ destinationId: 'a', landmarkIds: ['x', 'y'] }) // 'a' (destination takes priority)
 */
export function getPlaceIdsForSearchApi(filters: {
  destinationId?: string
  destinationIds?: Array<string>
  landmarkId?: string
  landmarkIds?: Array<string>
}): string | undefined {
  const destinationIds = getDestinationIds(filters)
  if (destinationIds && destinationIds.length > 0) {
    return formatIdsForSearchApi(destinationIds)
  }

  const landmarkIds = getLandmarkIds(filters)
  if (landmarkIds && landmarkIds.length > 0) {
    return formatIdsForSearchApi(landmarkIds)
  }

  return undefined
}
